'use strict';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import * as T from 'prop-types';
import React from 'react';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import {Observable} from 'rxjs/Observable';
import {ImagenieUtil} from '../../../_libs/imagenie';
import fuse from '../../../_libs/redux-fusion';
import {AjaxFactory, HttpMethod} from '../../../_libs/redux-observable-list';
import {BASE_REDUX_LIST_OPTIONS} from '../../../config/redux-list';
import {epicErrorHandler} from '../../../core-utilities/rx-utils';
import {FileDataUrlCache} from '../../services/FileDataUrlCache';
import {DomainImageComponent} from '../DomainImage/DomainImageComponent';
import {logger} from '../logger';
import {ImageView} from './ImageView';


const imageFactory = buildImageFactory(BASE_REDUX_LIST_OPTIONS);

export const StreamedComponent = (state$, dispatch) => (props$) => {
  const request$ = props$
    .distinctUntilChanged(null, props => props.url)
    .switchMap(props => {
      if (!ImageContainer.isInternalUrl(props.url)) {
        return Observable.of({isLoading: false, err: null});
      } else {
        const cacheId = getUrl(props);
        const cachedUrl = ImageContainer.dependencies.fileDataUrlCache.get(cacheId);
        if (cachedUrl) {
          return Observable.of({
            url: cachedUrl,
            isLoading: false,
          });
        } else {
          return Observable.merge(
            Observable.of({isLoading: true, err: null}),
            state$.take(1)
              .switchMap(rootState =>
                imageFactory.sendRequest({
                  url: getUrl(props),
                }, rootState)
              )
              .switchMap(ajaxResponse => {
                const signedUrl = ajaxResponse.response ? ajaxResponse.response.url : null;
                if (signedUrl && props.cacheBase64) {
                  return ImagenieUtil.getImageBase64String(signedUrl)
                    .then(cacheResult(cacheId))
                    .then(dataUrl => ({
                      url: dataUrl,
                      isLoading: false,
                    }));
                } else {
                  return Observable.of({
                    url: signedUrl,
                    isLoading: false,
                  });
                }
              })
              .catch(expoBackoff({
                logger,
                logAlways: false,
                message: 'requestSyncList',
                mapTo: err => Observable.of({isLoading: false, err}),
                performRetry: (err, errorCount) => errorCount < 10 && isRecoverable(err.status),
              })),
          );
        }
      }
    })
    .catch(epicErrorHandler(logger, 'ImageContainer.state$'));

  return props$
    .combineLatest(
      request$,
      (props, request) => {
        const isInternal = ImageContainer.isInternalUrl(props.url);
        return {
          ...props,
          style: DomainImageComponent.getStyle(props),
          isLoading: isInternal ? request.isLoading : false,
          hasError: isInternal ? !!request.err : false,
          url: isInternal ? request.url : props.url,
        };
      }
    );
};

export const ImageContainer = fuse(StreamedComponent)(ImageView);

ImageContainer.propTypes = {
  className: T.string,
  title: T.string,
  url: T.string,
  crop: T.bool,
  cacheBase64: T.bool,
  targetHeight: T.number,
  targetWidth: T.number,
  displayWidth: T.number,
  displayHeight: T.number,
};

ImageContainer.isInternalUrl = url => url ? url.charAt(0) === '/' && url.charAt(1) !== '/' : false;

ImageContainer.$inject = [
  FileDataUrlCache.SERVICE_NAME,
];

function getUrl(props) {
  const params = [];
  if (props.crop) {
    params.push(`${encodeURIComponent('crop')}=${encodeURIComponent('true')}`);
  }

  if (isNumber(props.targetHeight) && isNumber(props.targetWidth)) {
    params.push(`${encodeURIComponent('height')}=${encodeURIComponent(props.targetHeight)}`);
    params.push(`${encodeURIComponent('width')}=${encodeURIComponent(props.targetWidth)}`);
  }

  return params.length > 0 ? `${props.url}?${params.join('&')}` : props.url;
}

function cacheResult(cacheId) {
  return dataUrl => {
    if (dataUrl) {
      ImageContainer.dependencies.fileDataUrlCache.put(cacheId, dataUrl);
    }

    return dataUrl;
  };
}

// utils

// TODO: move this into the appropriate utils method and update existing usages to use the options param
/**
 * @param {Object} options
 * @param options.logger
 * @param options.logAlways If false, only logs the last error when performRetry() === false. Otherwise, logs every error.
 * @param options.message The error log message
 * @param options.performRetry
 * @param options.mapTo
 * @return {function(*=, *): *}
 */
function expoBackoff(options) {
  let errorCount = 0;
  return (err, source$) => {
    const doRetry = options.performRetry(err, errorCount);

    if (!doRetry || (doRetry && options.logAlways)) {
      options.logger.error({err}, options.message);
    }

    return doRetry
      ? Observable.timer(++errorCount * 5000)
        .mergeMap(() => source$)
      : getErrorHandlerFallback(options.mapTo);
  };
}

function getErrorHandlerFallback(err, mapTo) {
  let result;
  if (isFunction(mapTo)) {
    result = mapTo(err);
  } else {
    result = mapTo;
  }
  return isUndefined(result) ? Observable.empty() : Observable.of(result);
}

function isRecoverable(status) {
  return status === 0 || status === 202 || (status >= 500 && status < 600);
}

// start main code here

function buildImageFactory(options) {
  return new AjaxFactory(buildImageRequestOptions(options));
}

/**
 * @param options
 * @return {AjaxFactoryOptions}
 */
function buildImageRequestOptions(options) {
  return {
    crossDomain: options.crossDomain,
    method: HttpMethod.GET,
    useBody: false,
    url: getUrl,
    getToken: options.getToken,
  };

  function getUrl(request) {
    if (request.url && request.url.charAt(0) === '/') {
      return `${options.baseServerUrl}${request.url}`;
    } else {
      return request.url;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/files/components/Image/ImageContainer.js